import type { AddressInput, RecipientFormDraft, RegionOption } from "@/interfaces/redemption";
import { regionAbbr } from "@/utils/address-profiles";

/**
 * Address-form helpers for the recipient-details stage: the confirmation-review
 * address formatting and the typed-address lines for the Smarty verification modal.
 * (Autocomplete is Google Places — see components/redemption/RedemptionAddressAutocomplete.)
 */

/**
 * Confirmation-review address lines (name, street, apt, city/region/postal, country,
 * phone), formatted per the country's ordering. `postalBeforeCity` renders the EU
 * order ("10115 Berlin") instead of "City, Region Postal".
 */
export function buildReviewAddressLines(
  form: RecipientFormDraft,
  opts: { regions: RegionOption[]; postalBeforeCity: boolean; countryName: string; isUS: boolean },
): string[] {
  const name = `${form.firstName} ${form.lastName}`.trim();
  const region = regionAbbr(opts.regions, form.state);
  const cityLine = opts.postalBeforeCity
    ? [form.zipCode, form.city].filter(Boolean).join(" ") // EU order: "10115 Berlin"
    : [form.city, [region, form.zipCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const lines: string[] = [];
  if (name) lines.push(name);
  if (form.address) lines.push(form.address);
  if (form.apartment) lines.push(form.apartment);
  if (cityLine) lines.push(cityLine);
  if (!opts.isUS) lines.push(opts.countryName);
  if (form.phone) lines.push(`Phone: ${form.phoneDial} ${form.phone}`);
  return lines;
}

/**
 * The typed street address as compact display lines ("123 Main St Apt 4" then
 * "City, ST 12345") — the "You entered" column of the Smarty verification modal.
 */
export function addressInputLines(input: AddressInput): string[] {
  return [
    input.apartment ? `${input.address} ${input.apartment}` : input.address,
    [input.city, [input.state, input.zipCode].filter(Boolean).join(" ")].filter(Boolean).join(", "),
  ].filter(Boolean);
}
